import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Page, PageCursorMetadata } from '@tran-monorepo/interfaces';
import { CreateFile, File as FileData, FileStatus } from '@tran-monorepo/interfaces/files';
import { filter, Observable, switchMap, take, tap, timer } from 'rxjs';

export const APPLICATION_API_URL = new InjectionToken<string>('APPLICATION_API_URL');

@Injectable({
    providedIn: 'root'
})
export class ApplicationFilesService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = inject(APPLICATION_API_URL);

    private readonly pollingIntervalMs = 1000;

    public listFiles(
        applicationId: string,
        cursor?: string,
        limit = 20
    ): Observable<Page<FileData, PageCursorMetadata>> {
        let params = new HttpParams({ fromObject: { limit } });
        if (cursor) {
            params = params.set('cursor', cursor);
        }

        return this.http.get<Page<FileData, PageCursorMetadata>>(this.getApplicationFilesUrl(applicationId), {
            params
        });
    }

    public getFileData(applicationId: string, fileId: string): Observable<FileData> {
        return this.http.get<FileData>(`${this.getApplicationFilesUrl(applicationId)}/${fileId}`);
    }

    public createFileData(applicationId: string, fileData: CreateFile, file: File): Observable<FileData> {
        let fileId: string;

        return this.http
            .post<FileData & { signedURL: string }>(this.getApplicationFilesUrl(applicationId), fileData)
            .pipe(
                tap(createdFile => (fileId = createdFile.id)),
                switchMap(createdFile =>
                    this.http.put(createdFile.signedURL, file, {
                        headers: {
                            'Content-Type': file.type || 'application/octet-stream'
                        },
                        responseType: 'text'
                    })
                ),
                switchMap(() => timer(0, this.pollingIntervalMs)),
                switchMap(() => this.http.get<FileData>(`${this.getApplicationFilesUrl(applicationId)}/${fileId}`)),
                filter(
                    fileData =>
                        fileData.status === FileStatus.AV_SCAN_DONE || fileData.status === FileStatus.AV_SCAN_FAILED
                ),
                take(1)
            );
    }

    public updateFileData(applicationId: string, fileId: string, fileData: Partial<CreateFile>): Observable<FileData> {
        return this.http.patch<FileData>(`${this.getApplicationFilesUrl(applicationId)}/${fileId}`, fileData);
    }

    public deleteFile(applicationId: string, fileId: string): Observable<void> {
        return this.http.delete<void>(`${this.getApplicationFilesUrl(applicationId)}/${fileId}`);
    }

    public getFileDownloadUrl(
        applicationId: string,
        fileId: string,
        downloadFileName?: string
    ): Observable<{ signedUrl: string }> {
        let params = new HttpParams();
        if (downloadFileName) {
            params = params.set('downloadFileName', downloadFileName);
        }

        return this.http.get<{ signedUrl: string }>(
            `${this.getApplicationFilesUrl(applicationId)}/${fileId}/download-link`,
            {
                params
            }
        );
    }

    private getApplicationFilesUrl(applicationId: string): string {
        return `${this.apiUrl}/${applicationId}/files`;
    }
}
